import React, { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { SEND_MESSAGE_V2 } from '../../../graphql/operations';
import './widgets.css';

function splitLabeledText(value) {
  const text = String(value || '');
  const separator = text.indexOf(':');
  if (separator <= 0) return null;
  return {
    label: text.slice(0, separator),
    value: text.slice(separator + 1).trim()
  };
}

/**
 * PatientCard — renders patient info from widget data.
 *
 * Handles widgetTypes: patient_result, patient_summary, patient_match, patient_duplicate
 *
 * widgetData.children array follows the ChatKit Card children structure:
 * Title, Badge, Text (with optional weight:"semibold"), Divider, Caption, Button, Spacer
 */
function PatientCard({ data, widgetType, conversationId, messageId, onConfirmationSubmitted }) {
  const [buttonState, setButtonState] = useState({ phase: 'idle', id: null });
  const [sendMessage] = useMutation(SEND_MESSAGE_V2);
  const children = useMemo(() => data.children || [], [data.children]);
  const statusText = data.status?.text || 'Patient';
  const titleChild = children.find((c) => c.type === 'Title');

  const headerClass = widgetType === 'patient_result'
    ? 'widget-header-success'
    : widgetType === 'patient_duplicate'
      ? 'widget-header-warning'
      : 'widget-header-info';

  const isDisambiguation = widgetType === 'patient_match';
  const selectablePatientIds = useMemo(
    () => children
      .filter((child) => child.type === 'Button')
      .map((child) => child.onClickAction?.payload?.patient?.uidForClient || '')
      .filter(Boolean),
    [children]
  );
  const disambiguationStateKey = useMemo(() => {
    // Require messageId to scope persistence to a single prompt instance.
    if (!isDisambiguation || !conversationId || !messageId || selectablePatientIds.length === 0) return null;
    const pendingActionId = String(
      children.find((child) => child.type === 'Button')?.onClickAction?.payload?.pendingActionId || ''
    ).trim();
    return [
      'kapsa:disambiguation-selection:v1',
      conversationId,
      messageId,
      widgetType,
      pendingActionId,
      selectablePatientIds.join('|')
    ].join(':');
  }, [children, conversationId, isDisambiguation, messageId, selectablePatientIds, widgetType]);

  useEffect(() => {
    if (!disambiguationStateKey || typeof window === 'undefined') {
      setButtonState((prev) => ((prev.phase === 'idle' && !prev.id) ? prev : { phase: 'idle', id: null }));
      return;
    }
    const persistedId = String(window.sessionStorage.getItem(disambiguationStateKey) || '').trim();
    if (persistedId && selectablePatientIds.includes(persistedId)) {
      setButtonState((prev) => ((prev.phase === 'done' && prev.id === persistedId) ? prev : { phase: 'done', id: persistedId }));
      return;
    }
    setButtonState((prev) => ((prev.phase === 'idle' && !prev.id) ? prev : { phase: 'idle', id: null }));
  }, [disambiguationStateKey, selectablePatientIds]);

  useEffect(() => {
    if (!disambiguationStateKey || typeof window === 'undefined') return;
    if (buttonState.phase === 'done' && buttonState.id) {
      window.sessionStorage.setItem(disambiguationStateKey, buttonState.id);
      return;
    }
    if (buttonState.phase === 'idle' || buttonState.phase === 'loading') {
      window.sessionStorage.removeItem(disambiguationStateKey);
    }
  }, [buttonState.id, buttonState.phase, disambiguationStateKey]);

  return (
    <div className={`widget-card ${isDisambiguation ? 'widget-card--disambiguation' : ''}`} data-widget-type={widgetType}>
      <div className={`widget-card-status-header ${headerClass}`}>
        <span className="widget-status-dot" />
        <span className="widget-status-text">{statusText}</span>
      </div>

      <div className="widget-card-content">
        {children.map((child, i) => {
          switch (child.type) {
            case 'Title':
              return <h4 key={i} className={`widget-card-title ${isDisambiguation ? 'widget-card-record-title' : ''}`}>{child.value}</h4>;
            case 'Badge':
              return (
                <span key={i} className={`widget-badge widget-badge-${child.color || 'primary'}`}>
                  {child.label}
                </span>
              );
            case 'Text':
              {
                const labeled = isDisambiguation ? splitLabeledText(child.value) : null;
              return (
                <p key={i} className={`widget-text ${child.weight === 'semibold' ? 'widget-text-bold' : ''} ${isDisambiguation ? 'widget-text-structured' : ''}`}>
                  {labeled ? <span className="widget-text-label">{labeled.label}:</span> : null}
                  {labeled ? <span className="widget-text-value">{labeled.value}</span> : child.value}
                </p>
              );
              }
            case 'Caption':
              return <p key={i} className="widget-caption">{child.value}</p>;
            case 'Divider':
              return <hr key={i} className="widget-divider" />;
            case 'Spacer':
              return <div key={i} className="widget-spacer" />;
            case 'Button': {
              const patientId = child.onClickAction?.payload?.patient?.uidForClient || '';
              const pendingActionId = child.onClickAction?.payload?.pendingActionId || '';
              const isLoading = buttonState.phase === 'loading' && buttonState.id === patientId;
              const isDone = buttonState.phase === 'done' && buttonState.id === patientId;
              const isDisabled = !conversationId || !patientId || buttonState.phase === 'loading' || buttonState.phase === 'done';

              return (
                <button
                  key={i}
                  type="button"
                  className={`widget-btn widget-btn-primary ${isDisambiguation ? 'widget-btn-record' : ''} ${isLoading ? 'is-loading' : ''} ${isDone ? 'is-done' : ''}`}
                  disabled={isDisabled}
                  aria-label={`Select ${titleChild?.value || 'patient'}`}
                  onClick={async () => {
                    if (!conversationId || !patientId || isDisabled) return;
                    setButtonState({ phase: 'loading', id: patientId });
                    try {
                      const command = pendingActionId
                        ? `/select-patient ${patientId} ${pendingActionId}`
                        : `/select-patient ${patientId}`;
                      await sendMessage({
                        variables: {
                          conversationId,
                          content: command
                        }
                      });
                      setButtonState({ phase: 'done', id: patientId });
                      onConfirmationSubmitted?.();
                    } catch (error) {
                      console.error('Failed to select patient:', error);
                      setButtonState({ phase: 'idle', id: null });
                    }
                  }}
                >
                  {isLoading && <span className="widget-btn-spinner" aria-hidden="true" />}
                  {isLoading ? 'Selecting...' : isDone ? 'Selected' : child.label}
                </button>
              );
            }
            default:
              return null;
          }
        })}
      </div>
    </div>
  );
}

export default PatientCard;
